import * as React from 'react';
import moment from 'moment-timezone';
import { assertNever } from '@deltasierra/shared';

type TimestampFormat = 'descriptive-date' | 'long' | 'short';

function getDateWithTimezone(
    dateOrString: Date | string,
    timezone?: string,
): { date: moment.Moment } & ({ withTimezone: false } | { withTimezone: true; timezone: string }) {
    const date = moment(dateOrString);
    const timezoneTrimmed = timezone?.trim();

    if (!timezoneTrimmed || timezoneTrimmed === '') {
        return {
            date,
            withTimezone: false,
        };
    }
    return {
        date: date.tz(timezoneTrimmed),
        timezone: timezoneTrimmed,
        withTimezone: true,
    };
}

function getDateString(dateOrString: Date | string, format: TimestampFormat, _timezone?: string): string {
    const { date, ...tz } = getDateWithTimezone(dateOrString, _timezone);
    switch (format) {
        case 'short': {
            const isSameYear = date.toDate().getFullYear() === new Date().getFullYear();
            const yearFormat = isSameYear ? '' : ' YY';
            return date.format(`Do MMM${yearFormat}`);
        }
        case 'long': {
            const formattedDate = date.format('D MMM YYYY HH:mm:ss');
            const timezoneFormat = tz.withTimezone ? `(${tz.timezone}): ` : '';
            return `${timezoneFormat}${formattedDate}`;
        }
        case 'descriptive-date': {
            const formattedDate = date.format('ddd, D MMM, YYYY');
            const timezoneFormat = tz.withTimezone ? `(${tz.timezone}): ` : '';
            return `${timezoneFormat}${formattedDate}`;
        }
        default:
            return assertNever(format);
    }
}

export interface TimestampProps {
    date: Date | string;
    format?: TimestampFormat;
    timezone?: string;
}

export const Timestamp: React.FC<TimestampProps> = ({ date, format = 'short', timezone }) => {
    const dateString: string = getDateString(date, format, timezone);
    return <>{dateString}</>;
};
Timestamp.displayName = 'Timestamp';
